import { TenantContext } from '@authe/fe-commons/tenancy';
import { createLogger } from '@authe/fe-commons/logging';
import { initials } from '@authe/fe-commons/display';
import * as React3 from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createMountSlot } from '@authe/fe-commons/lifecycle';
import { useNow, TooltipProvider, Tooltip, TooltipTrigger, Avatar, TooltipContent, Textarea, Button, Badge, Input } from '@authe/ui-core';
import { formatRelativeTime } from '@authe/fe-commons/time';
import { useDepsChanged } from '@authe/ui-core/api';

// src/model/scope.ts
var COLLAB_PACKAGE = "collaboration";
var log = createLogger(`fe:${COLLAB_PACKAGE}`);
var COLLAB_CHANNEL_PREFIX = "collab";
var COLLAB_BROADCAST_PREFIX = "authe:collab";
var AWARENESS_TIMEOUT_MS = 3e4;
var AWARENESS_THROTTLE_MS = 80;
var COLLAB_IDB_PREFIX = "authe-collab";
var COLLAB_PROTOCOL_VERSION = 1;

// src/model/scope.ts
function seg(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === "" || trimmed === "main" ? null : trimmed;
}
function roomId(scope) {
  const ctx = new TenantContext(scope.tenant);
  const parts = [COLLAB_CHANNEL_PREFIX, ctx.orgIdTenantId];
  if (ctx.customerId) parts.push("customer", encodeURIComponent(ctx.customerId));
  const team = seg(scope.teamId);
  if (team) parts.push("team", team);
  parts.push("ws", scope.workspaceId);
  return parts.join(":");
}
function sameRoom(a, b) {
  return roomId(a) === roomId(b);
}

// src/util.ts
var nodeBuffer = globalThis.Buffer;
function bytesToBase64(bytes) {
  if (typeof btoa === "function") {
    let binary = "";
    const chunk = 32768;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }
  return nodeBuffer ? nodeBuffer.from(bytes).toString("base64") : "";
}
function base64ToBytes(b64) {
  if (typeof atob === "function") {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
    return out;
  }
  return nodeBuffer ? new Uint8Array(nodeBuffer.from(b64, "base64")) : new Uint8Array();
}
function randomId(prefix = "") {
  const uuid = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return prefix ? `${prefix}_${uuid}` : uuid;
}
function presenceColor(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 45%)`;
}

// src/model/user.ts
function userColor(user) {
  return user.color ?? presenceColor(user.id);
}
function userInitials(name) {
  return initials(name);
}

// src/model/comment.ts
var CommentAnchorKind = {
  WORKSPACE: "workspace",
  NODE: "node",
  EDGE: "edge",
  ROW: "row",
  STEP: "step",
  CUSTOM: "custom"
};
var WORKSPACE_ANCHOR = { kind: CommentAnchorKind.WORKSPACE, id: "_root" };
function sameAnchor(a, b) {
  return a.kind === b.kind && a.id === b.id;
}
function anchorFor(kind, id, label) {
  return label === void 0 ? { kind, id } : { kind, id, label };
}

// src/model/workspace.ts
var WorkspaceKind = {
  /** Co-edited transaction graph / investigation canvas. */
  GRAPH: "graph",
  /** A saved investigation (notes + linked entities + graph). */
  INVESTIGATION: "investigation",
  /** Building/tuning a transaction-processing pipeline together. */
  PIPELINE: "pipeline",
  /** Coordinating a smart-contract upgrade / proxy migration runbook. */
  CONTRACT_UPGRADE: "contract_upgrade",
  /** Watchlist / portfolio of DeFi positions or NFT holdings. */
  PORTFOLIO: "portfolio",
  /** Generic shared document. */
  GENERIC: "generic"
};
var DocKey = {
  /** `Y.Map` — title/kind/owner metadata. */
  META: "meta",
  /** `Y.Map` — free-form app state (graph layout, pipeline draft, selection…). */
  STATE: "state",
  /** `Y.Array<Y.Map>` — anchored comments + threaded replies. */
  COMMENTS: "comments",
  /** `Y.Array<Y.Map>` — approval workflow steps. */
  WORKFLOW: "workflow"
};

// src/model/workflow.ts
var WorkflowStepStatus = {
  TODO: "todo",
  IN_PROGRESS: "in_progress",
  BLOCKED: "blocked",
  DONE: "done"
};
var StepGate = {
  /** No approval required. */
  NONE: "none",
  /** Needs N approvals from collaborators. */
  APPROVAL: "approval",
  /** Needs a second factor / two-key signature before it can be marked done. */
  TWO_FACTOR: "two_factor"
};
function isStepApproved(step) {
  if (step.gate === StepGate.NONE) return true;
  const unique = new Set(step.approvals.map((a) => a.userId));
  return unique.size >= Math.max(1, step.requiredApprovals);
}
function canCompleteStep(step) {
  return isStepApproved(step);
}

// src/transport/transport.ts
var CollabMsg = {
  /** Incremental Yjs document update. */
  UPDATE: "u",
  /** "Here's my state vector — send me what I'm missing." */
  SYNC_REQUEST: "sq",
  /** Targeted reply to a sync request (a diff for the requester). */
  SYNC_RESPONSE: "sr",
  /** Awareness (presence/cursor) update. */
  AWARENESS: "a"
};
function createHealthSource(initial) {
  let current = initial;
  const listeners = /* @__PURE__ */ new Set();
  return {
    get: () => current,
    set(health) {
      if (health.status === current.status && health.reason === current.reason && health.title === current.title) {
        return;
      }
      current = health;
      for (const listener of listeners) listener(current);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}
var IMPLIED_LIVE = { status: "live" };
var IMPLIED_LOCAL = {
  status: "local-only",
  reason: "This browser only \u2014 nothing is being sent to your team."
};
function transportHealth(transport) {
  return transport.health?.() ?? (transport.reach === "remote" ? IMPLIED_LIVE : IMPLIED_LOCAL);
}
function combineTransports(...transports) {
  const remote = transports.filter((t) => t.reach === "remote");
  const health = createHealthSource(
    remote.length > 0 ? { status: "live" } : { status: "local-only", reason: "This browser only \u2014 nothing is being sent to your team." }
  );
  const broken = /* @__PURE__ */ new Set();
  const recompute = () => {
    if (remote.length === 0) return;
    const reasons = [];
    let failing = 0;
    let connecting = 0;
    remote.forEach((t, index) => {
      const own = transportHealth(t);
      const threw = broken.has(index);
      if (own.status === "connecting" && !threw) {
        connecting += 1;
        if (own.reason && !reasons.includes(own.reason)) reasons.push(own.reason);
        return;
      }
      if (own.status !== "live" || threw) {
        failing += 1;
        const reason = threw ? "the channel rejected the last update" : own.reason;
        if (reason && !reasons.includes(reason)) reasons.push(reason);
      }
    });
    if (failing > 0) {
      health.set({
        status: "failed",
        reason: reasons.join("; ") || (failing === remote.length ? "None of your team channels are carrying updates." : "One of your team channels is not carrying updates.")
      });
      return;
    }
    if (connecting > 0) {
      health.set({
        status: "connecting",
        reason: reasons.join("; ") || "Connecting to your team\u2019s collaboration channel\u2026"
      });
      return;
    }
    health.set({ status: "live" });
  };
  const offs = remote.map(
    (t, index) => t.subscribeHealth?.(() => {
      broken.delete(index);
      recompute();
    })
  );
  const hydrating = transports.filter((t) => t.hydrate);
  const combined = {
    reach: remote.length > 0 ? "remote" : "local",
    durable: transports.some((t) => t.durable),
    // One leg that needs complete states makes the whole fan-out need them: a
    // full state is a valid update for every leg, an incremental delta is not.
    fullStateUpdates: transports.some((t) => t.fullStateUpdates),
    // Presence reaches other PEOPLE only over a remote leg, and only if that
    // leg relays awareness at all — see `carriesAwareness`.
    carriesAwareness: remote.some((t) => t.carriesAwareness !== false),
    health: () => health.get(),
    subscribeHealth: (listener) => health.subscribe(listener),
    publish(env) {
      transports.forEach((t) => {
        try {
          void t.publish(env);
        } catch {
          const index = remote.indexOf(t);
          if (index >= 0) broken.add(index);
        }
      });
      recompute();
    },
    subscribe(room, handler) {
      const subs = transports.map((t) => t.subscribe(room, handler));
      return () => {
        for (const off of subs) off();
      };
    },
    close() {
      for (const off of offs) off?.();
      for (const t of transports) t.close?.();
    }
  };
  if (hydrating.length > 0) {
    combined.hydrate = async (room, apply, signal) => {
      for (const t of hydrating) await t.hydrate?.(room, apply, signal);
    };
  }
  return combined;
}

// src/transport/broadcastTransport.ts
function createBroadcastTransport(options = {}) {
  const rooms = /* @__PURE__ */ new Map();
  const hasBC = typeof BroadcastChannel !== "undefined";
  const health = {
    status: options.status ?? "local-only",
    reason: options.reason ?? (options.title ? void 0 : hasBC ? "Other tabs of this browser only \u2014 nothing is being sent to your team." : "This tab only \u2014 nothing is being sent to your team."),
    ...options.title ? { title: options.title } : {}
  };
  return {
    // A cross-tab channel is not a network leg, and saying so is the whole
    // point: everything above this can now tell "synced" from "seen by me".
    reach: "local",
    health: () => health,
    publish(env) {
      rooms.get(env.room)?.channel.postMessage(env);
    },
    subscribe(room, handler) {
      if (!hasBC) return () => {
      };
      let entry = rooms.get(room);
      if (!entry) {
        const channel = new BroadcastChannel(`${COLLAB_BROADCAST_PREFIX}:${room}`);
        entry = { channel, handlers: /* @__PURE__ */ new Set() };
        channel.onmessage = (event) => {
          const env = event.data;
          for (const h of entry.handlers) h(env);
        };
        rooms.set(room, entry);
      }
      entry.handlers.add(handler);
      return () => {
        const current = rooms.get(room);
        if (!current) return;
        current.handlers.delete(handler);
        if (current.handlers.size === 0) {
          current.channel.close();
          rooms.delete(room);
        }
      };
    },
    close() {
      for (const { channel } of rooms.values()) channel.close();
      rooms.clear();
    }
  };
}

// src/transport/appsyncTransport.ts
function defaultDecode(payload) {
  let value = payload;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (value && typeof value === "object" && "t" in value && "room" in value && "from" in value) {
    return value;
  }
  return null;
}
function createStreamingCollabTransport(options) {
  const decode = options.decode ?? defaultDecode;
  const health = createHealthSource({ status: options.client.getState ? "connecting" : "live" });
  const channels = /* @__PURE__ */ new Map();
  const subscriptions = /* @__PURE__ */ new Set();
  let offState;
  let publishFailed = false;
  const refreshHealth = () => {
    if (publishFailed) {
      health.set({ status: "failed", reason: "Your last update was rejected by the collaboration channel." });
      return;
    }
    const state = options.client.getState?.();
    if (!state) {
      health.set({ status: "live" });
      return;
    }
    const selected = [...channels.keys()].map((id) => state.channels?.[id]);
    const failed = selected.find((channel) => channel?.status === "error");
    if (failed || state.status === "error") {
      health.set({ status: "failed", reason: failed?.error ?? state.lastError ?? "Your collaboration subscription failed." });
    } else if ((state.status === "open" || state.status === "degraded") && selected.length > 0 && selected.every((channel) => channel?.status === "open")) {
      health.set({ status: "live" });
    } else {
      health.set({ status: "connecting", reason: "Connecting to your collaboration channel." });
    }
  };
  return {
    reach: "remote",
    health: () => health.get(),
    subscribeHealth: (listener) => health.subscribe(listener),
    async publish(env) {
      try {
        await options.publish(env);
        publishFailed = false;
        refreshHealth();
      } catch (error) {
        log.warn("appsync publish failed", { room: env.room, t: env.t, error: String(error) });
        publishFailed = true;
        refreshHealth();
      }
    },
    subscribe(room, handler) {
      const descriptor = options.channelFor(room);
      channels.set(descriptor.id, (channels.get(descriptor.id) ?? 0) + 1);
      offState ??= options.client.onState?.(refreshHealth);
      const unsubscribe = options.client.subscribeChannel(descriptor, (message) => {
        const env = decode(message.payload);
        if (env && env.room === room) handler(env);
      });
      refreshHealth();
      let active = true;
      const off = () => {
        if (!active) return;
        active = false;
        subscriptions.delete(off);
        const count = (channels.get(descriptor.id) ?? 1) - 1;
        if (count) channels.set(descriptor.id, count);
        else channels.delete(descriptor.id);
        unsubscribe();
        if (channels.size === 0) {
          offState?.();
          offState = void 0;
        }
        refreshHealth();
      };
      subscriptions.add(off);
      return off;
    },
    close() {
      for (const off of subscriptions) off();
      offState?.();
      offState = void 0;
    }
  };
}

// src/yjs/lazy.ts
var cached = null;
function loadYjs() {
  if (!cached) {
    cached = Promise.all([import('yjs'), import('y-protocols/awareness')]).then(
      ([Y, awareness]) => ({ Y, awareness })
    );
  }
  return cached;
}
async function loadIndexeddbPersistence() {
  if (typeof indexedDB === "undefined") return null;
  try {
    const mod = await import(
      /* @vite-ignore */
      'y-indexeddb'
    );
    return mod.IndexeddbPersistence;
  } catch {
    return null;
  }
}

// src/yjs/doc.ts
function createDocHandle(YMod, roomId2) {
  const doc = new YMod.Doc({ guid: roomId2 });
  return {
    roomId: roomId2,
    doc,
    meta: () => doc.getMap(DocKey.META),
    state: () => doc.getMap(DocKey.STATE),
    comments: () => doc.getArray(DocKey.COMMENTS),
    workflow: () => doc.getArray(DocKey.WORKFLOW)
  };
}
function seedMeta(handle, meta) {
  const map = handle.meta();
  if (map.get("kind")) return;
  handle.doc.transact(() => {
    map.set("title", meta.title);
    map.set("kind", meta.kind);
    if (meta.chainId !== void 0) map.set("chainId", meta.chainId);
    if (meta.createdBy) map.set("createdBy", meta.createdBy);
    map.set("createdAt", meta.createdAt ?? Date.now());
    map.set("updatedAt", Date.now());
  });
}
function readMeta(handle) {
  return handle.meta().toJSON();
}

// src/yjs/persistence.ts
var NOOP = {
  whenSynced: Promise.resolve(),
  destroy: () => {
  },
  clear: () => Promise.resolve()
};
async function attachPersistence(roomId2, doc) {
  const Provider = await loadIndexeddbPersistence();
  if (!Provider) {
    log.debug("indexeddb persistence unavailable \u2014 running memory-only", { roomId: roomId2 });
    return NOOP;
  }
  const dbName = `${COLLAB_IDB_PREFIX}:${roomId2}`;
  const provider = new Provider(dbName, doc);
  return {
    whenSynced: provider.whenSynced.then(() => void 0),
    destroy: () => provider.destroy(),
    clear: () => provider.clearData()
  };
}

// src/provider/engine.ts
var REMOTE_ORIGIN = "collab:remote";
var CollabEngine = class {
  constructor(deps) {
    this.deps = deps;
  }
  deps;
  teardown = null;
  clog = log.child("engine");
  start() {
    if (this.teardown) return;
    const { Y: YMod, awarenessMod, handle, awareness, transport, clientId } = this.deps;
    const room = handle.roomId;
    const onDocUpdate = (update, origin) => {
      if (origin === REMOTE_ORIGIN || this.deps.readOnly) return;
      const payload = transport.fullStateUpdates ? YMod.encodeStateAsUpdate(handle.doc) : update;
      void transport.publish(this.envelope(CollabMsg.UPDATE, bytesToBase64(payload)));
    };
    handle.doc.on("update", onDocUpdate);
    const throttleMs = this.deps.awarenessThrottleMs ?? AWARENESS_THROTTLE_MS;
    const pendingClients = /* @__PURE__ */ new Set();
    let awarenessTimer = null;
    let lastAwarenessAt = -Infinity;
    const flushAwareness = () => {
      if (awarenessTimer) clearTimeout(awarenessTimer);
      awarenessTimer = null;
      if (pendingClients.size === 0) return;
      const changed = [...pendingClients];
      pendingClients.clear();
      lastAwarenessAt = Date.now();
      const enc = awarenessMod.encodeAwarenessUpdate(awareness, changed);
      void transport.publish(this.envelope(CollabMsg.AWARENESS, bytesToBase64(enc)));
    };
    const onAwareness = (changes, origin) => {
      if (origin === REMOTE_ORIGIN) return;
      for (const id of [...changes.added, ...changes.updated, ...changes.removed]) pendingClients.add(id);
      if (changes.removed.length > 0) {
        flushAwareness();
        return;
      }
      if (awarenessTimer) return;
      awarenessTimer = setTimeout(flushAwareness, Math.max(0, lastAwarenessAt + throttleMs - Date.now()));
    };
    awareness.on("update", onAwareness);
    const offSub = transport.subscribe(room, (env) => this.onEnvelope(env));
    void transport.publish(
      this.envelope(CollabMsg.SYNC_REQUEST, bytesToBase64(YMod.encodeStateVector(handle.doc)))
    );
    if (!this.deps.readOnly && this.deps.publishInitialState !== false) {
      void transport.publish(this.envelope(CollabMsg.UPDATE, bytesToBase64(YMod.encodeStateAsUpdate(handle.doc))));
    }
    this.teardown = () => {
      handle.doc.off("update", onDocUpdate);
      awareness.off("update", onAwareness);
      if (awarenessTimer) clearTimeout(awarenessTimer);
      awarenessTimer = null;
      offSub();
    };
    this.clog.debug("started", { room, clientId });
  }
  stop() {
    const { awarenessMod, awareness } = this.deps;
    awarenessMod.removeAwarenessStates(awareness, [awareness.clientID], "stop");
    this.teardown?.();
    this.teardown = null;
  }
  envelope(t, data, to) {
    const env = {
      v: COLLAB_PROTOCOL_VERSION,
      room: this.deps.handle.roomId,
      from: this.deps.clientId,
      t,
      data
    };
    if (to) env.to = to;
    return env;
  }
  onEnvelope(env) {
    if (env.from === this.deps.clientId) return;
    if (env.v !== COLLAB_PROTOCOL_VERSION) return;
    if (env.room !== this.deps.handle.roomId) return;
    const { Y: YMod, awarenessMod, handle, awareness, transport, clientId } = this.deps;
    const doc = handle.doc;
    switch (env.t) {
      case CollabMsg.UPDATE:
        YMod.applyUpdate(doc, base64ToBytes(env.data), REMOTE_ORIGIN);
        return;
      case CollabMsg.SYNC_REQUEST: {
        const diff = YMod.encodeStateAsUpdate(doc, base64ToBytes(env.data));
        void transport.publish(this.envelope(CollabMsg.SYNC_RESPONSE, bytesToBase64(diff), env.from));
        const localStates = [...awareness.getStates().keys()];
        if (localStates.length > 0) {
          const enc = awarenessMod.encodeAwarenessUpdate(awareness, localStates);
          void transport.publish(this.envelope(CollabMsg.AWARENESS, bytesToBase64(enc)));
        }
        return;
      }
      case CollabMsg.SYNC_RESPONSE:
        if (env.to === clientId) YMod.applyUpdate(doc, base64ToBytes(env.data), REMOTE_ORIGIN);
        return;
      case CollabMsg.AWARENESS:
        awarenessMod.applyAwarenessUpdate(awareness, base64ToBytes(env.data), REMOTE_ORIGIN);
        return;
    }
  }
};

// src/provider/setup.ts
async function setupCollaboration(opts) {
  const { Y, awareness: awarenessMod } = await loadYjs();
  const room = roomId(opts.scope);
  const handle = createDocHandle(Y, room);
  const awareness = new awarenessMod.Awareness(handle.doc);
  const clientId = randomId("c");
  const initial = { user: opts.user, focus: null, active: true, ts: Date.now() };
  awareness.setLocalState(initial);
  let persistence = null;
  let whenReady = Promise.resolve();
  if (opts.persist) {
    persistence = await attachPersistence(`${room}:user:${opts.user.id}`, handle.doc);
    whenReady = persistence.whenSynced;
  }
  const remote = opts.transport.hydrate ? new Y.Doc() : null;
  let publishInitialState = true;
  try {
    await whenReady;
    opts.signal?.throwIfAborted();
    await opts.transport.hydrate?.(room, (update) => {
      if (remote) Y.applyUpdate(remote, update);
      Y.applyUpdate(handle.doc, update, REMOTE_ORIGIN);
    }, opts.signal);
    opts.signal?.throwIfAborted();
    if (remote) {
      const saved = Y.encodeStateAsUpdate(remote);
      const local = Y.encodeStateAsUpdate(handle.doc);
      publishInitialState = saved.length !== local.length || saved.some((byte, i) => byte !== local[i]);
    }
  } catch (error) {
    persistence?.destroy();
    awareness.destroy();
    handle.doc.destroy();
    throw error;
  } finally {
    remote?.destroy();
  }
  const engine = new CollabEngine({
    Y,
    awarenessMod,
    handle,
    awareness,
    transport: opts.transport,
    clientId,
    readOnly: opts.readOnly,
    publishInitialState
  });
  engine.start();
  if (opts.meta && !opts.readOnly) {
    seedMeta(handle, { ...opts.meta, createdBy: opts.user.id });
  }
  const setPresence = (patch) => {
    const current = awareness.getLocalState() ?? {};
    awareness.setLocalState({ ...current, ...patch, ts: Date.now() });
  };
  log.debug("session ready", { room, kind: opts.meta?.kind ?? WorkspaceKind.GENERIC });
  return {
    roomId: room,
    handle,
    awareness,
    engine,
    clientId,
    self: opts.user,
    readOnly: opts.readOnly ?? false,
    Y,
    whenReady,
    setPresence,
    destroy: () => {
      engine.stop();
      persistence?.destroy();
      awareness.destroy();
      handle.doc.destroy();
    }
  };
}
function useYjsValue(target, compute, deep = false) {
  const [, force] = React3.useReducer((n) => n + 1, 0);
  React3.useEffect(() => {
    const handler = () => force();
    if (deep) {
      target.observeDeep(handler);
      return () => target.unobserveDeep(handler);
    }
    target.observe(handler);
    return () => target.unobserve(handler);
  }, [target, deep]);
  return compute();
}

// src/provider/delivery.ts
var COLLAB_DELIVERY_UNKNOWN = {
  status: "local-only",
  reason: "This browser only \u2014 nothing is being sent to your team.",
  canDeliver: false,
  sharesPresence: false,
  durable: false
};
function deliveryFrom(health, transport) {
  const durable = Boolean(transport.durable);
  const deliverable = health.status === "live" || health.status === "connecting" || health.status === "failed" && durable;
  return {
    status: health.status,
    reason: health.reason,
    ...health.title ? { title: health.title } : {},
    canDeliver: deliverable,
    sharesPresence: transport.carriesAwareness !== false && health.status === "live",
    durable
  };
}
function deliveryOf(transport) {
  if (!transport) return COLLAB_DELIVERY_UNKNOWN;
  return deliveryFrom(transportHealth(transport), transport);
}
function deliveryMessage(delivery) {
  if (delivery.status === "live") return null;
  if (delivery.status === "connecting") {
    return {
      title: "Connecting to your team",
      detail: delivery.reason ?? "Anything you write now is sent once the channel is ready."
    };
  }
  if (delivery.status === "failed") {
    return delivery.durable ? { title: "Sync failed \xB7 retrying", detail: "Keep this tab open until it reconnects." } : {
      title: "Your team isn\u2019t receiving updates",
      detail: delivery.reason ?? "The collaboration channel rejected the last update."
    };
  }
  if (delivery.title) {
    return delivery.reason ? { title: delivery.title, detail: delivery.reason } : { title: delivery.title };
  }
  return {
    title: "Not connected to your team",
    detail: delivery.reason ?? COLLAB_DELIVERY_UNKNOWN.reason
  };
}
var CollabDeliveryContext = React3.createContext(null);
function useTransportDelivery(transport) {
  const subscribe = React3.useCallback(
    (onChange) => transport?.subscribeHealth?.(() => onChange()) ?? (() => {
    }),
    [transport]
  );
  const snapshot = React3.useCallback(
    () => transport ? transportHealth(transport) : null,
    [transport]
  );
  const health = React3.useSyncExternalStore(subscribe, snapshot, snapshot);
  return React3.useMemo(
    () => transport && health ? deliveryFrom(health, transport) : COLLAB_DELIVERY_UNKNOWN,
    [transport, health]
  );
}
function CollabDeliveryProvider({
  value,
  children
}) {
  return /* @__PURE__ */ jsx(CollabDeliveryContext.Provider, { value, children });
}
function useCollabStatus() {
  return React3.useContext(CollabDeliveryContext) ?? COLLAB_DELIVERY_UNKNOWN;
}
function CollabStatusNote({ className }) {
  const delivery = useCollabStatus();
  const message = deliveryMessage(delivery);
  if (!message) return null;
  const tone = delivery.status === "failed" && !delivery.durable ? "text-destructive" : "text-muted-foreground";
  return /* @__PURE__ */ jsxs("p", { role: "status", className: `text-xs ${tone} ${className ?? ""}`, children: [
    /* @__PURE__ */ jsx("span", { className: "font-medium", children: message.title }),
    message.detail ? /* @__PURE__ */ jsxs("span", { children: [
      " \xB7 ",
      message.detail
    ] }) : null
  ] });
}
var CollaborationContext = React3.createContext(null);
function CollabSessionProvider({
  session,
  children
}) {
  return /* @__PURE__ */ jsx(CollaborationContext.Provider, { value: session, children });
}
function CollaborationProvider(props) {
  const { scope, user, transport, meta, persist, readOnly, fallback, errorFallback, children } = props;
  const [session, setSession] = React3.useState(null);
  const [failure, setFailure] = React3.useState(false);
  const [attempt, setAttempt] = React3.useState(0);
  const latest = React3.useRef({ user, meta });
  latest.current = { user, meta };
  const [transportSlot] = React3.useState(() => createMountSlot());
  const acquireTransport = React3.useCallback(
    () => transportSlot.acquire(
      () => createBroadcastTransport({
        // Said out loud, because this fallback is the headline defect's cause:
        // a host that wires no transport gets a channel that reaches its own
        // tabs and nothing else, and every surface used to render that as
        // ordinary collaboration.
        reason: "This app hasn\u2019t wired a live collaboration channel for this workspace."
      })
    ),
    [transportSlot]
  );
  const [effectiveTransport, setEffectiveTransport] = React3.useState(null);
  const delivery = useTransportDelivery(effectiveTransport);
  const depsKey = `${roomId(scope)}|${user.id}|${persist ? 1 : 0}|${readOnly ? 1 : 0}`;
  React3.useEffect(() => {
    let alive = true;
    let created = null;
    const controller = new AbortController();
    setSession(null);
    setFailure(false);
    const running = transport ?? acquireTransport();
    setEffectiveTransport(running);
    setupCollaboration({
      scope,
      user: latest.current.user,
      transport: running,
      meta: latest.current.meta,
      persist,
      readOnly,
      signal: controller.signal
    }).then((s) => {
      if (!alive) {
        s.destroy();
        return;
      }
      created = s;
      setSession(s);
    }).catch((error) => {
      if (!alive) return;
      setFailure(true);
      log.error(`collaboration setup failed: ${String(error)}`, { room: roomId(scope) });
    });
    return () => {
      alive = false;
      controller.abort();
      created?.destroy();
    };
  }, [depsKey, transport, acquireTransport, attempt]);
  React3.useEffect(
    () => () => {
      const owned = transportSlot.peek();
      if (owned && transportSlot.release(owned)) owned.close?.();
    },
    [transportSlot]
  );
  if (failure) return /* @__PURE__ */ jsxs("div", { role: "alert", className: "p-6 text-sm", children: [
    /* @__PURE__ */ jsx("p", { children: "Couldn\u2019t restore the workspace. Editing is paused until its history loads." }),
    /* @__PURE__ */ jsx("button", { type: "button", className: "mt-2 underline", onClick: () => setAttempt((n) => n + 1), children: "Try again" }),
    errorFallback
  ] });
  if (!session || session.roomId !== roomId(scope) || session.self.id !== user.id || session.readOnly !== Boolean(readOnly)) return /* @__PURE__ */ jsx(Fragment, { children: fallback ?? null });
  return /* @__PURE__ */ jsx(CollabSessionProvider, { session, children: /* @__PURE__ */ jsx(CollabDeliveryProvider, { value: delivery, children }) });
}
function useCollaboration() {
  const session = React3.useContext(CollaborationContext);
  if (!session) {
    throw new Error("useCollaboration must be used within a ready <CollaborationProvider>");
  }
  return session;
}
function useOptionalCollaboration() {
  return React3.useContext(CollaborationContext);
}
function usePresence(options = {}) {
  const { includeSelf = false, trackVisibility = true } = options;
  const session = useCollaboration();
  const delivery = useCollabStatus();
  const { awareness, setPresence } = session;
  const [version, bump] = React3.useReducer((n) => n + 1, 0);
  React3.useEffect(() => {
    const handler = () => bump();
    awareness.on("change", handler);
    return () => awareness.off("change", handler);
  }, [awareness]);
  React3.useEffect(() => {
    if (!trackVisibility || typeof document === "undefined") return;
    const onVisibility = () => setPresence({ active: !document.hidden });
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [trackVisibility, setPresence]);
  const selfClientId = awareness.clientID;
  const now = useNow(AWARENESS_TIMEOUT_MS / 2);
  const cutoff = now - AWARENESS_TIMEOUT_MS;
  const peers = React3.useMemo(() => {
    const list = [];
    let localPeer = null;
    for (const [clientId, raw] of awareness.getStates()) {
      const state = raw;
      if (!state || !state.user) continue;
      const lastUpdated = awareness.meta.get(clientId)?.lastUpdated ?? state.ts;
      if (clientId !== selfClientId && typeof lastUpdated === "number" && lastUpdated < cutoff) continue;
      const peer = { ...state, clientId, isSelf: clientId === selfClientId };
      if (peer.isSelf) localPeer = peer;
      if (!peer.isSelf || includeSelf) list.push(peer);
    }
    return { list, self: localPeer };
  }, [awareness, version, cutoff, includeSelf, selfClientId]);
  const all = peers.list;
  const self = peers.self;
  const setFocus = React3.useCallback(
    (anchor) => setPresence({ focus: anchor }),
    [setPresence]
  );
  const setCursor = React3.useCallback((cursor) => setPresence({ cursor }), [setPresence]);
  const setActive = React3.useCallback(
    (active) => setPresence({ active }),
    [setPresence]
  );
  const focusedOn = React3.useCallback(
    (anchor) => all.filter((p) => p.focus && sameAnchor(p.focus, anchor)),
    [all]
  );
  return {
    peers: all,
    self,
    isolated: !delivery.sharesPresence,
    unavailableReason: delivery.sharesPresence ? void 0 : delivery.reason,
    setFocus,
    setCursor,
    setActive,
    focusedOn
  };
}
function focusLabel(peer) {
  if (!peer.focus) return peer.active ? "viewing the workspace" : "idle";
  const { kind, label, id } = peer.focus;
  return `on ${kind} ${label ?? id}`;
}
function PresenceAvatars(props) {
  const { max = 5, size = "sm", className } = props;
  const fallbackPeers = usePresence({ includeSelf: true });
  const peers = props.peers ?? fallbackPeers.peers;
  const isolated = fallbackPeers.isolated;
  const shown = peers.slice(0, max);
  const overflow = peers.length - shown.length;
  return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsxs("div", { className: `flex items-center gap-3 ${className ?? ""}`, children: [
    /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center -space-x-2", children: [
      shown.map((peer) => /* @__PURE__ */ jsxs(Tooltip, { children: [
        /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
          "span",
          {
            className: "inline-flex shrink-0 rounded-full leading-none ring-2 ring-background transition-opacity",
            style: {
              boxShadow: `0 0 0 2px ${userColor(peer.user)}`,
              opacity: peer.active ? 1 : 0.45
            },
            children: /* @__PURE__ */ jsx(
              Avatar,
              {
                src: peer.user.avatarUrl,
                alt: peer.user.name,
                size,
                fallback: userInitials(peer.user.name)
              }
            )
          }
        ) }),
        /* @__PURE__ */ jsxs(TooltipContent, { children: [
          /* @__PURE__ */ jsxs("span", { className: "font-medium", children: [
            peer.user.name,
            peer.isSelf ? " (you)" : ""
          ] }),
          /* @__PURE__ */ jsx("span", { className: "block text-xs text-muted-foreground", children: focusLabel(peer) }),
          isolated ? /* @__PURE__ */ jsx("span", { className: "block text-xs text-muted-foreground", children: "Presence isn\u2019t being shared \u2014 this is your own session only." }) : null
        ] })
      ] }, peer.clientId)),
      overflow > 0 ? /* @__PURE__ */ jsxs("span", { className: "z-10 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground ring-2 ring-background", children: [
        "+",
        overflow
      ] }) : null
    ] }),
    isolated ? /* @__PURE__ */ jsx(
      "span",
      {
        role: "status",
        className: "text-[10px] font-medium uppercase tracking-wide text-muted-foreground",
        title: `Viewer presence is unavailable in this session. This does not change who can read or edit the shared document.${fallbackPeers.unavailableReason ? ` ${fallbackPeers.unavailableReason}` : ""}`,
        children: "Presence unavailable"
      }
    ) : null
  ] }) });
}

// src/mentions/parse.ts
var MENTION_RE = /@\[([^\]]+)\]\(([^)]+)\)/g;
function parseMentions(body) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const match of body.matchAll(MENTION_RE)) {
    const display = match[1];
    const userId = match[2];
    if (seen.has(userId)) continue;
    seen.add(userId);
    out.push({ userId, display });
  }
  return out;
}
function toSegments(body) {
  const segments = [];
  let last = 0;
  for (const match of body.matchAll(MENTION_RE)) {
    const start = match.index ?? 0;
    if (start > last) segments.push({ type: "text", text: body.slice(last, start) });
    segments.push({ type: "mention", display: match[1], userId: match[2] });
    last = start + match[0].length;
  }
  if (last < body.length) segments.push({ type: "text", text: body.slice(last) });
  return segments;
}
function mentionToken(userId, display) {
  return `@[${display}](${userId})`;
}
function toPlainText(body) {
  return body.replace(MENTION_RE, (_m, display) => `@${display}`);
}
function activeMentionQuery(text, caret) {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const query = upto.slice(at + 1);
  if (/[\]\s]/.test(query)) return null;
  return { query, at };
}
function MentionInput(props) {
  const {
    value,
    onChange,
    users,
    placeholder,
    onSubmit,
    disabled,
    rows = 3,
    autoFocus,
    className,
    maxSuggestions = 6
  } = props;
  const ref = React3.useRef(null);
  const pendingCaret = React3.useRef(null);
  const [query, setQuery] = React3.useState(null);
  const [activeIndex, setActiveIndex] = React3.useState(0);
  React3.useLayoutEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      const pos = pendingCaret.current;
      pendingCaret.current = null;
      ref.current.setSelectionRange(pos, pos);
    }
  });
  const { suggestions, hidden } = React3.useMemo(() => {
    if (!query) return { suggestions: [], hidden: 0 };
    const q = query.text.toLowerCase();
    const matches = users.filter((u) => {
      if (!q) return true;
      return u.name.toLowerCase().includes(q) || (u.handle ?? "").toLowerCase().includes(q) || u.id.toLowerCase().includes(q);
    });
    return {
      suggestions: matches.slice(0, maxSuggestions),
      hidden: Math.max(0, matches.length - maxSuggestions)
    };
  }, [query, users, maxSuggestions]);
  const open = query !== null && suggestions.length > 0;
  const recomputeQuery = React3.useCallback((text, caret) => {
    const found = activeMentionQuery(text, caret);
    setQuery(found ? { text: found.query, at: found.at } : null);
    setActiveIndex(0);
  }, []);
  const handleChange = (event) => {
    const next = event.target.value;
    onChange(next);
    recomputeQuery(next, event.target.selectionStart ?? next.length);
  };
  const select = (user) => {
    if (!query) return;
    const el = ref.current;
    const caret = el?.selectionStart ?? value.length;
    const token = mentionToken(user.id, user.name);
    const next = `${value.slice(0, query.at)}${token} ${value.slice(caret)}`;
    pendingCaret.current = query.at + token.length + 1;
    onChange(next);
    setQuery(null);
  };
  const handleKeyDown = (event) => {
    if (open) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        const picked = suggestions[activeIndex];
        if (picked) select(picked);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setQuery(null);
        return;
      }
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSubmit?.();
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: `relative ${className ?? ""}`, children: [
    /* @__PURE__ */ jsx(
      Textarea,
      {
        ref,
        value,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onKeyUp: (e) => recomputeQuery(e.currentTarget.value, e.currentTarget.selectionStart ?? 0),
        onClick: (e) => recomputeQuery(e.currentTarget.value, e.currentTarget.selectionStart ?? 0),
        placeholder,
        disabled,
        rows,
        autoFocus
      }
    ),
    open ? /* @__PURE__ */ jsxs(
      "ul",
      {
        role: "listbox",
        className: "absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-auto rounded-md border bg-popover p-1 shadow-md",
        children: [
          suggestions.map((user, index) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              role: "option",
              "aria-selected": index === activeIndex,
              onMouseDown: (e) => {
                e.preventDefault();
                select(user);
              },
              onMouseEnter: () => setActiveIndex(index),
              className: `flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm ${index === activeIndex ? "bg-accent text-accent-foreground" : ""}`,
              children: [
                /* @__PURE__ */ jsx(
                  Avatar,
                  {
                    src: user.avatarUrl,
                    alt: user.name,
                    size: "sm",
                    fallback: userInitials(user.name)
                  }
                ),
                /* @__PURE__ */ jsxs("span", { className: "flex flex-col leading-tight", children: [
                  /* @__PURE__ */ jsx("span", { className: "font-medium", children: user.name }),
                  user.handle ? /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
                    "@",
                    user.handle
                  ] }) : null
                ] })
              ]
            }
          ) }, user.id)),
          hidden > 0 ? /* @__PURE__ */ jsxs(
            "li",
            {
              "aria-live": "polite",
              className: "px-2 py-1.5 text-xs text-muted-foreground",
              children: [
                "+",
                hidden,
                " more ",
                hidden === 1 ? "match" : "matches",
                " \u2014 keep typing to narrow the list."
              ]
            }
          ) : null
        ]
      }
    ) : null
  ] });
}
function commentFromMap(map) {
  return {
    id: map.get("id"),
    anchor: map.get("anchor"),
    authorId: map.get("authorId"),
    body: map.get("body"),
    mentions: map.get("mentions") ?? [],
    createdAt: map.get("createdAt"),
    editedAt: map.get("editedAt"),
    resolved: map.get("resolved") ?? false,
    resolvedBy: map.get("resolvedBy"),
    parentId: map.get("parentId")
  };
}
function useComments(options = {}) {
  const { anchor } = options;
  const session = useCollaboration();
  const delivery = useCollabStatus();
  const arr = session.handle.comments();
  const all = useYjsValue(
    arr,
    () => {
      const list = [];
      for (let i = 0; i < arr.length; i += 1) list.push(commentFromMap(arr.get(i)));
      return list.sort((a, b) => a.createdAt - b.createdAt);
    },
    true
  );
  const comments = React3.useMemo(
    () => anchor ? all.filter((c) => sameAnchor(c.anchor, anchor)) : all,
    [all, anchor]
  );
  const threads = React3.useMemo(() => {
    const tops = comments.filter((c) => !c.parentId);
    return tops.map((comment) => ({
      comment,
      replies: comments.filter((c) => c.parentId === comment.id)
    }));
  }, [comments]);
  const findIndex = React3.useCallback(
    (id) => {
      for (let i = 0; i < arr.length; i += 1) {
        if (arr.get(i).get("id") === id) return i;
      }
      return -1;
    },
    [arr]
  );
  const add = React3.useCallback(
    (body, opts) => {
      if (session.readOnly) return null;
      if (!delivery.canDeliver) return null;
      const text = body.trim();
      if (!text) return null;
      const target = opts?.anchor ?? anchor ?? WORKSPACE_ANCHOR;
      const map = new session.Y.Map();
      const comment = {
        id: randomId("cm"),
        anchor: target,
        authorId: session.self.id,
        body: text,
        mentions: parseMentions(text),
        createdAt: Date.now(),
        parentId: opts?.parentId
      };
      session.handle.doc.transact(() => {
        map.set("id", comment.id);
        map.set("anchor", comment.anchor);
        map.set("authorId", comment.authorId);
        map.set("body", comment.body);
        map.set("mentions", comment.mentions);
        map.set("createdAt", comment.createdAt);
        if (comment.parentId) map.set("parentId", comment.parentId);
        arr.push([map]);
      });
      return comment;
    },
    [anchor, arr, delivery.canDeliver, session]
  );
  const edit = React3.useCallback(
    (id, body) => {
      if (session.readOnly) return;
      const index = findIndex(id);
      if (index < 0) return;
      const map = arr.get(index);
      session.handle.doc.transact(() => {
        map.set("body", body);
        map.set("mentions", parseMentions(body));
        map.set("editedAt", Date.now());
      });
    },
    [arr, findIndex, session]
  );
  const resolve = React3.useCallback(
    (id) => {
      if (session.readOnly) return;
      const index = findIndex(id);
      if (index < 0) return;
      const map = arr.get(index);
      session.handle.doc.transact(() => {
        map.set("resolved", true);
        map.set("resolvedBy", session.self.id);
      });
    },
    [arr, findIndex, session]
  );
  const reopen = React3.useCallback(
    (id) => {
      if (session.readOnly) return;
      const index = findIndex(id);
      if (index < 0) return;
      const map = arr.get(index);
      session.handle.doc.transact(() => {
        map.set("resolved", false);
        map.set("resolvedBy", void 0);
      });
    },
    [arr, findIndex, session]
  );
  const remove = React3.useCallback(
    (id) => {
      if (session.readOnly) return;
      session.handle.doc.transact(() => {
        for (let i = arr.length - 1; i >= 0; i -= 1) {
          const map = arr.get(i);
          if (map.get("id") === id || map.get("parentId") === id) arr.delete(i, 1);
        }
      });
    },
    [arr, session]
  );
  const openCount = threads.filter((t) => !t.comment.resolved).length;
  return { comments, threads, count: comments.length, openCount, add, edit, resolve, reopen, remove };
}
function CommentComposer(props) {
  const {
    users,
    onSubmit,
    placeholder = "Add a comment\u2026 use @ to mention",
    submitLabel = "Comment",
    autoFocus,
    disabled,
    rows = 3,
    onCancel,
    notice,
    disabledReason
  } = props;
  const [value, setValue] = React3.useState("");
  const [rejected, setRejected] = React3.useState(false);
  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    const accepted = onSubmit(text);
    if (accepted === false) {
      setRejected(true);
      return;
    }
    setRejected(false);
    setValue("");
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(
      MentionInput,
      {
        value,
        onChange: (next) => {
          setValue(next);
          setRejected(false);
        },
        users,
        placeholder,
        onSubmit: submit,
        autoFocus,
        disabled,
        rows
      }
    ),
    notice,
    rejected ? /* @__PURE__ */ jsxs("p", { role: "alert", className: "text-xs text-destructive", children: [
      "Your comment wasn\u2019t sent",
      disabledReason ? ` \u2014 ${disabledReason}` : "",
      ". It is still in the box; try again once this is fixed."
    ] }) : null,
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-2", children: [
      onCancel ? /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: onCancel, children: "Cancel" }) : null,
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          onClick: submit,
          disabled: disabled || !value.trim(),
          title: disabled ? disabledReason : void 0,
          children: submitLabel
        }
      )
    ] })
  ] });
}
function CommentBody({
  body,
  resolveUser
}) {
  return /* @__PURE__ */ jsx("p", { className: "whitespace-pre-wrap break-words text-sm", children: toSegments(body).map(
    (seg2, i) => seg2.type === "mention" ? /* @__PURE__ */ jsxs(
      "span",
      {
        className: "rounded bg-primary/10 px-1 font-medium text-primary",
        title: resolveUser(seg2.userId)?.name ?? seg2.userId,
        children: [
          "@",
          resolveUser(seg2.userId)?.name ?? seg2.display
        ]
      },
      i
    ) : /* @__PURE__ */ jsx(React3.Fragment, { children: seg2.text }, i)
  ) });
}
function CommentCard(props) {
  const { comment, resolveUser, canResolve, isAuthor } = props;
  const author = resolveUser(comment.authorId);
  return /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
    /* @__PURE__ */ jsx(
      Avatar,
      {
        src: author?.avatarUrl,
        alt: author?.name ?? comment.authorId,
        size: "sm",
        fallback: userInitials(author?.name ?? comment.authorId)
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: author?.name ?? comment.authorId }),
        /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
          formatRelativeTime(comment.createdAt, { unit: "milliseconds" }),
          comment.editedAt ? " (edited)" : ""
        ] }),
        comment.resolved ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "ml-auto", children: "Resolved" }) : null
      ] }),
      /* @__PURE__ */ jsx(CommentBody, { body: comment.body, resolveUser }),
      /* @__PURE__ */ jsxs("div", { className: "mt-1 flex items-center gap-1", children: [
        props.onReply ? /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", className: "h-6 px-2 text-xs", onClick: props.onReply, children: "Reply" }) : null,
        canResolve && !comment.resolved && props.onResolve ? /* @__PURE__ */ jsx(
          Button,
          {
            variant: "ghost",
            size: "sm",
            className: "h-6 px-2 text-xs",
            onClick: props.onResolve,
            children: "Resolve"
          }
        ) : null,
        comment.resolved && props.onReopen ? /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", className: "h-6 px-2 text-xs", onClick: props.onReopen, children: "Reopen" }) : null,
        isAuthor && props.onDelete ? /* @__PURE__ */ jsx(
          Button,
          {
            variant: "ghost",
            size: "sm",
            className: "h-6 px-2 text-xs text-destructive",
            onClick: props.onDelete,
            children: "Delete"
          }
        ) : null
      ] })
    ] })
  ] });
}
function CommentThread(props) {
  const { anchor, users, hideResolved, title = "Comments", className } = props;
  const session = useCollaboration();
  const delivery = useCollabStatus();
  const { threads, openCount, add, resolve, reopen, remove } = useComments({ anchor });
  const [replyingTo, setReplyingTo] = React3.useState(null);
  const undeliverable = deliveryMessage(delivery);
  const blockedReason = delivery.canDeliver ? void 0 : undeliverable?.detail ?? undeliverable?.title;
  const resolveUser = React3.useMemo(() => {
    const index = new Map(users.map((u) => [u.id, u]));
    if (!index.has(session.self.id)) index.set(session.self.id, session.self);
    return props.resolveUser ?? ((id) => index.get(id));
  }, [users, props.resolveUser, session.self]);
  const visible = hideResolved ? threads.filter((t) => !t.comment.resolved) : threads;
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-col gap-4 ${className ?? ""}`, children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold", children: title }),
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
        openCount,
        " open"
      ] })
    ] }),
    session.readOnly ? /* @__PURE__ */ jsx(CollabStatusNote, {}) : null,
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      visible.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "No comments yet." }) : null,
      visible.map((thread) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
        /* @__PURE__ */ jsx(
          CommentCard,
          {
            comment: thread.comment,
            resolveUser,
            canResolve: !session.readOnly,
            isAuthor: !session.readOnly && thread.comment.authorId === session.self.id,
            onResolve: () => resolve(thread.comment.id),
            onReopen: session.readOnly ? void 0 : () => reopen(thread.comment.id),
            onReply: session.readOnly ? void 0 : () => setReplyingTo(thread.comment.id),
            onDelete: () => remove(thread.comment.id)
          }
        ),
        thread.replies.length > 0 ? /* @__PURE__ */ jsx("div", { className: "ml-8 flex flex-col gap-3 border-l pl-3", children: thread.replies.map((reply) => /* @__PURE__ */ jsx(
          CommentCard,
          {
            comment: reply,
            resolveUser,
            canResolve: false,
            isAuthor: !session.readOnly && reply.authorId === session.self.id,
            onDelete: () => remove(reply.id)
          },
          reply.id
        )) }) : null,
        replyingTo === thread.comment.id ? /* @__PURE__ */ jsx("div", { className: "ml-8", children: /* @__PURE__ */ jsx(
          CommentComposer,
          {
            users,
            autoFocus: true,
            rows: 2,
            submitLabel: "Reply",
            placeholder: "Reply\u2026 use @ to mention",
            onCancel: () => setReplyingTo(null),
            disabled: !delivery.canDeliver,
            disabledReason: blockedReason,
            onSubmit: (body) => {
              const written = add(body, { parentId: thread.comment.id });
              if (!written) return false;
              setReplyingTo(null);
              return true;
            }
          }
        ) }) : null
      ] }, thread.comment.id))
    ] }),
    !session.readOnly ? /* @__PURE__ */ jsx(
      CommentComposer,
      {
        users,
        disabled: !delivery.canDeliver,
        disabledReason: blockedReason,
        notice: /* @__PURE__ */ jsx(CollabStatusNote, {}),
        onSubmit: (body) => Boolean(add(body))
      }
    ) : null
  ] });
}
function useSharedState(key, fallback) {
  const session = useCollaboration();
  const map = session.handle.state();
  const value = useYjsValue(map, () => {
    const stored = map.get(key);
    return stored === void 0 ? fallback : stored;
  });
  const setValue = React3.useCallback(
    (next) => {
      if (session?.readOnly) return;
      const current = map.get(key);
      const resolved = typeof next === "function" ? next(current === void 0 ? fallback : current) : next;
      map.set(key, resolved);
    },
    [map, key, fallback, session]
  );
  return [value, setValue];
}
function useOptionalSharedState(key, fallback) {
  const session = useOptionalCollaboration();
  const map = session?.handle.state() ?? null;
  const [local, setLocal] = React3.useState(fallback);
  if (useDepsChanged([key])) setLocal(fallback);
  const shared = useYjsValue(map ?? NO_TARGET, () => {
    if (!map) return void 0;
    const stored = map.get(key);
    return stored === void 0 ? fallback : stored;
  });
  const setValue = React3.useCallback(
    (next) => {
      if (session?.readOnly) return;
      if (!map) {
        setLocal(
          (current2) => typeof next === "function" ? next(current2) : next
        );
        return;
      }
      const current = map.get(key);
      const resolved = typeof next === "function" ? next(current === void 0 ? fallback : current) : next;
      map.set(key, resolved);
    },
    [map, key, fallback, session]
  );
  return [map ? shared : local, setValue];
}
var NO_TARGET = {
  observe: () => {
  },
  unobserve: () => {
  },
  observeDeep: () => {
  },
  unobserveDeep: () => {
  }
};
function useWorkspaceMeta() {
  const session = useCollaboration();
  const map = session.handle.meta();
  const meta = useYjsValue(map, () => readMeta(session.handle));
  const setTitle = React3.useCallback(
    (title) => {
      if (session.readOnly) return;
      session.handle.doc.transact(() => {
        map.set("title", title);
        map.set("updatedAt", Date.now());
      });
    },
    [map, session]
  );
  const patch = React3.useCallback(
    (next) => {
      if (session.readOnly) return;
      session.handle.doc.transact(() => {
        for (const [k, v] of Object.entries(next)) map.set(k, v);
        map.set("updatedAt", Date.now());
      });
    },
    [map, session]
  );
  return { meta, setTitle, patch };
}
function readApprovals(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value.forEach === "function") {
    const out = [];
    value.forEach((approval, userId) => {
      if (approval) out.push({ ...approval, userId });
    });
    return out.sort((a, b) => a.at - b.at);
  }
  return [];
}
function stepFromMap(map) {
  return {
    id: map.get("id"),
    title: map.get("title"),
    description: map.get("description"),
    status: map.get("status"),
    gate: map.get("gate"),
    requiredApprovals: map.get("requiredApprovals") ?? 0,
    approvals: readApprovals(map.get("approvals")),
    assigneeId: map.get("assigneeId"),
    ref: map.get("ref"),
    createdAt: map.get("createdAt"),
    updatedAt: map.get("updatedAt")
  };
}
function useWorkflow() {
  const session = useCollaboration();
  const delivery = useCollabStatus();
  const arr = session.handle.workflow();
  const canEdit = !session.readOnly && delivery.canDeliver;
  const readOnlyReason = session.readOnly ? "This session is read-only, so sign-offs cannot be recorded." : delivery.canDeliver ? void 0 : delivery.reason ?? "Changes here would reach nobody on your team.";
  const steps = useYjsValue(
    arr,
    () => {
      const list = [];
      for (let i = 0; i < arr.length; i += 1) list.push(stepFromMap(arr.get(i)));
      return list;
    },
    true
  );
  const findIndex = React3.useCallback(
    (id) => {
      for (let i = 0; i < arr.length; i += 1) if (arr.get(i).get("id") === id) return i;
      return -1;
    },
    [arr]
  );
  const addStep = React3.useCallback(
    (input) => {
      if (!canEdit) return null;
      const now = Date.now();
      const gate = input.gate ?? StepGate.NONE;
      const step = {
        id: randomId("wf"),
        title: input.title,
        description: input.description,
        status: WorkflowStepStatus.TODO,
        gate,
        requiredApprovals: input.requiredApprovals ?? (gate === StepGate.NONE ? 0 : 1),
        approvals: [],
        assigneeId: input.assigneeId,
        ref: input.ref,
        createdAt: now,
        updatedAt: now
      };
      const map = new session.Y.Map();
      session.handle.doc.transact(() => {
        map.set("id", step.id);
        map.set("title", step.title);
        if (step.description) map.set("description", step.description);
        map.set("status", step.status);
        map.set("gate", step.gate);
        map.set("requiredApprovals", step.requiredApprovals);
        map.set("approvals", new session.Y.Map());
        if (step.assigneeId) map.set("assigneeId", step.assigneeId);
        if (step.ref) map.set("ref", step.ref);
        map.set("createdAt", step.createdAt);
        map.set("updatedAt", step.updatedAt);
        arr.push([map]);
      });
      return step;
    },
    [arr, canEdit, session]
  );
  const updateStep = React3.useCallback(
    (id, patch) => {
      if (!canEdit) return;
      const index = findIndex(id);
      if (index < 0) return;
      const map = arr.get(index);
      session.handle.doc.transact(() => {
        for (const [k, v] of Object.entries(patch)) map.set(k, v);
        map.set("updatedAt", Date.now());
      });
    },
    [arr, canEdit, findIndex, session]
  );
  const setStatus = React3.useCallback(
    (id, status) => {
      if (!canEdit) {
        log.warn("workflow status change dropped \u2014 session cannot write", { id });
        return;
      }
      const index = findIndex(id);
      if (index < 0) return;
      const map = arr.get(index);
      const step = stepFromMap(map);
      if (status === WorkflowStepStatus.DONE && !canCompleteStep(step)) {
        log.warn("step not approved \u2014 cannot complete", { id, gate: step.gate });
        return;
      }
      session.handle.doc.transact(() => {
        map.set("status", status);
        map.set("updatedAt", Date.now());
      });
    },
    [arr, canEdit, findIndex, session]
  );
  const approvalsOf = React3.useCallback(
    (map) => {
      const current = map.get("approvals");
      if (current && typeof current.forEach === "function") {
        return current;
      }
      const upgraded = new session.Y.Map();
      for (const approval of readApprovals(current)) upgraded.set(approval.userId, approval);
      map.set("approvals", upgraded);
      return upgraded;
    },
    [session]
  );
  const approve = React3.useCallback(
    (id, opts) => {
      if (!canEdit) {
        log.warn("approval dropped \u2014 session cannot write", { id });
        return;
      }
      const index = findIndex(id);
      if (index < 0) return;
      const map = arr.get(index);
      session.handle.doc.transact(() => {
        const approvals = approvalsOf(map);
        if (approvals.has(session.self.id)) return;
        approvals.set(session.self.id, {
          userId: session.self.id,
          at: Date.now(),
          signatureDigest: opts?.signatureDigest
        });
        map.set("updatedAt", Date.now());
      });
    },
    [approvalsOf, arr, canEdit, findIndex, session]
  );
  const revokeApproval = React3.useCallback(
    (id) => {
      if (!canEdit) {
        log.warn("approval withdrawal dropped \u2014 session cannot write", { id });
        return;
      }
      const index = findIndex(id);
      if (index < 0) return;
      const map = arr.get(index);
      session.handle.doc.transact(() => {
        const approvals = approvalsOf(map);
        if (!approvals.has(session.self.id)) return;
        approvals.delete(session.self.id);
        map.set("updatedAt", Date.now());
      });
    },
    [approvalsOf, arr, canEdit, findIndex, session]
  );
  const removeStep = React3.useCallback(
    (id) => {
      if (!canEdit) return;
      const index = findIndex(id);
      if (index >= 0) arr.delete(index, 1);
    },
    [arr, canEdit, findIndex]
  );
  const reorder = React3.useCallback(
    (from, to) => {
      if (!canEdit) return;
      if (from === to || from < 0 || from >= arr.length) return;
      session.handle.doc.transact(() => {
        const [map] = arr.slice(from, from + 1);
        if (!map) return;
        const clone = new session.Y.Map();
        for (const [k, v] of map.entries()) {
          if (k === "approvals") {
            const approvals = new session.Y.Map();
            for (const approval of readApprovals(v)) approvals.set(approval.userId, approval);
            clone.set(k, approvals);
            continue;
          }
          clone.set(k, v);
        }
        arr.delete(from, 1);
        arr.insert(Math.min(to, arr.length), [clone]);
      });
    },
    [arr, canEdit, session]
  );
  const pendingCount = steps.filter((s) => s.status !== WorkflowStepStatus.DONE).length;
  return {
    steps,
    pendingCount,
    complete: steps.length > 0 && pendingCount === 0,
    canEdit,
    readOnlyReason,
    addStep,
    updateStep,
    setStatus,
    approve,
    revokeApproval,
    removeStep,
    reorder,
    isStepApproved,
    canCompleteStep
  };
}
var STATUS_LABEL = {
  [WorkflowStepStatus.TODO]: "To do",
  [WorkflowStepStatus.IN_PROGRESS]: "In progress",
  [WorkflowStepStatus.BLOCKED]: "Blocked",
  [WorkflowStepStatus.DONE]: "Done"
};
var STATUS_VARIANT = {
  [WorkflowStepStatus.TODO]: "outline",
  [WorkflowStepStatus.IN_PROGRESS]: "secondary",
  [WorkflowStepStatus.BLOCKED]: "destructive",
  [WorkflowStepStatus.DONE]: "default"
};
function nextStatus(status) {
  if (status === WorkflowStepStatus.TODO) return WorkflowStepStatus.IN_PROGRESS;
  if (status === WorkflowStepStatus.IN_PROGRESS) return WorkflowStepStatus.DONE;
  return WorkflowStepStatus.TODO;
}
function WorkflowChecklist(props) {
  const { editable, onRequestSignature, title = "Workflow", className } = props;
  const session = useCollaboration();
  const wf = useWorkflow();
  const [draft, setDraft] = React3.useState("");
  const [needsSignoff, setNeedsSignoff] = React3.useState(false);
  const approve = async (step) => {
    if (!wf.canEdit) return;
    if (step.gate === StepGate.TWO_FACTOR && onRequestSignature) {
      const digest = await onRequestSignature(step);
      if (!digest) return;
      wf.approve(step.id, { signatureDigest: digest });
      return;
    }
    wf.approve(step.id);
  };
  const addStep = () => {
    const titleText = draft.trim();
    if (!titleText || !wf.canEdit) return;
    wf.addStep({
      title: titleText,
      gate: needsSignoff ? StepGate.APPROVAL : StepGate.NONE,
      requiredApprovals: needsSignoff ? 2 : 0
    });
    setDraft("");
    setNeedsSignoff(false);
  };
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-col gap-3 ${className ?? ""}`, children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold", children: title }),
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
        wf.steps.length - wf.pendingCount,
        "/",
        wf.steps.length,
        " done"
      ] })
    ] }),
    !wf.canEdit ? /* @__PURE__ */ jsxs("p", { role: "status", className: "text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx("span", { className: "font-medium", children: "Sign-offs are disabled" }),
      wf.readOnlyReason ? /* @__PURE__ */ jsxs("span", { children: [
        " \xB7 ",
        wf.readOnlyReason
      ] }) : null
    ] }) : null,
    /* @__PURE__ */ jsxs("ul", { className: "flex flex-col gap-2", children: [
      wf.steps.map((step) => {
        const approved = isStepApproved(step);
        const hasApproved = step.approvals.some((a) => a.userId === session.self.id);
        const gated = step.gate !== StepGate.NONE;
        return /* @__PURE__ */ jsxs("li", { className: "flex items-start gap-2 rounded-md border p-2", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "aria-label": "advance status",
              className: "mt-0.5 disabled:cursor-not-allowed disabled:opacity-60",
              disabled: !wf.canEdit,
              title: wf.canEdit ? void 0 : wf.readOnlyReason,
              onClick: () => wf.setStatus(step.id, nextStatus(step.status)),
              children: /* @__PURE__ */ jsx(Badge, { variant: STATUS_VARIANT[step.status], children: STATUS_LABEL[step.status] })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: `text-sm ${step.status === WorkflowStepStatus.DONE ? "text-muted-foreground line-through" : ""}`,
                  children: step.title
                }
              ),
              gated ? /* @__PURE__ */ jsxs(Badge, { variant: approved ? "default" : "secondary", className: "text-[10px]", children: [
                step.gate === StepGate.TWO_FACTOR ? "2FA" : "Sign-off",
                " ",
                step.approvals.length,
                "/",
                step.requiredApprovals
              ] }) : null
            ] }),
            step.ref?.address ? /* @__PURE__ */ jsx("span", { className: "block font-mono text-xs text-muted-foreground", children: step.ref.address }) : null
          ] }),
          gated ? /* @__PURE__ */ jsx(
            Button,
            {
              variant: hasApproved ? "ghost" : "outline",
              size: "sm",
              className: "h-7 px-2 text-xs",
              disabled: !wf.canEdit,
              title: wf.canEdit ? void 0 : wf.readOnlyReason,
              onClick: () => hasApproved ? wf.revokeApproval(step.id) : void approve(step),
              children: hasApproved ? "Approved" : "Approve"
            }
          ) : null,
          editable ? /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "sm",
              className: "h-7 px-2 text-xs text-destructive",
              disabled: !wf.canEdit,
              title: wf.canEdit ? void 0 : wf.readOnlyReason,
              onClick: () => wf.removeStep(step.id),
              children: "Remove"
            }
          ) : null
        ] }, step.id);
      }),
      wf.steps.length === 0 ? /* @__PURE__ */ jsx("li", { className: "text-sm text-muted-foreground", children: "No steps yet." }) : null
    ] }),
    editable ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          value: draft,
          disabled: !wf.canEdit,
          onChange: (e) => setDraft(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addStep();
            }
          },
          placeholder: wf.canEdit ? "Add a step\u2026" : "Read-only \u2014 steps can\u2019t be added"
        }
      ),
      /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "checkbox",
            checked: needsSignoff,
            disabled: !wf.canEdit,
            onChange: (e) => setNeedsSignoff(e.target.checked)
          }
        ),
        "sign-off"
      ] }),
      /* @__PURE__ */ jsx(
        Button,
        {
          size: "sm",
          onClick: addStep,
          disabled: !draft.trim() || !wf.canEdit,
          title: wf.canEdit ? void 0 : wf.readOnlyReason,
          children: "Add"
        }
      )
    ] }) : null
  ] });
}

export { COLLAB_DELIVERY_UNKNOWN, COLLAB_PACKAGE, COLLAB_PROTOCOL_VERSION, CollabDeliveryProvider, CollabEngine, CollabMsg, CollabSessionProvider, CollabStatusNote, CollaborationProvider, CommentAnchorKind, CommentComposer, CommentThread, DocKey, MentionInput, PresenceAvatars, REMOTE_ORIGIN, StepGate, WORKSPACE_ANCHOR, WorkflowChecklist, WorkflowStepStatus, WorkspaceKind, activeMentionQuery, anchorFor, attachPersistence, canCompleteStep, combineTransports, createBroadcastTransport, createDocHandle, createHealthSource, createStreamingCollabTransport, deliveryFrom, deliveryMessage, deliveryOf, isStepApproved, loadIndexeddbPersistence, loadYjs, mentionToken, parseMentions, readMeta, roomId, sameAnchor, sameRoom, seedMeta, setupCollaboration, toPlainText, toSegments, transportHealth, useCollabStatus, useCollaboration, useComments, useOptionalCollaboration, useOptionalSharedState, usePresence, useSharedState, useTransportDelivery, useWorkflow, useWorkspaceMeta, useYjsValue, userColor, userInitials };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map